import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCreditCard, faEnvelope } from '@fortawesome/pro-regular-svg-icons';
import FormErrorHandler
  from '@avtechsoftware/react-component-library/src/components/common/FormErrorHandler/FormErrorHandler';
import AltErrorWrapper
  from '@avtechsoftware/react-component-library/src/components/common/FormErrorHandler/AltErrorWrapper/AltErrorWrapper';
import {
  SERVICE_PLAN_PAYMENT_METHOD,
} from '../../../../utility/types/ServicePlanWorkflowTypes';

const MONTHS = [
  { value: '01', display: '01 - Jan' },
  { value: '02', display: '02 - Feb' },
  { value: '03', display: '03 - Mar' },
  { value: '04', display: '04 - Apr' },
  { value: '05', display: '05 - May' },
  { value: '06', display: '06 - Jun' },
  { value: '07', display: '07 - Jul' },
  { value: '08', display: '08 - Aug' },
  { value: '09', display: '09 - Sep' },
  { value: '10', display: '10 - Oct' },
  { value: '11', display: '11 - Nov' },
  { value: '12', display: '12 - Dec' },
];

function buildYears() {
  const currYear = moment().year();
  const years = [];
  for (let i = 0; i < 30; i += 1) {
    years.push((currYear + i).toString());
  }
  return years;
}

const YEARS = buildYears();

function PaymentMethodForm({
  paymentMethod,
  onPaymentMethodChange,
  hasCardOnFile,
  cardOnFileDisplay,
  cardNumber,
  onCardNumberChange,
  expirationMonth,
  onExpirationMonthChange,
  expirationYear,
  onExpirationYearChange,
  cvv,
  onCvvChange,
  errorList,
  hideCheckOrBankTransfer,
}) {
  const showCardFields = paymentMethod === SERVICE_PLAN_PAYMENT_METHOD.NEW_CREDIT_CARD;
  const showCheckInfo = paymentMethod === SERVICE_PLAN_PAYMENT_METHOD.CHECK_OR_BANK_TRANSFER;
  const showSubmit = paymentMethod !== SERVICE_PLAN_PAYMENT_METHOD.CHECK_OR_BANK_TRANSFER;

  return (
    <div>
      <div className="row mb-3">
        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
        <label className="col-form-label col-sm-3 text-sm-right align-self-start"><b>Payment Method</b></label>
        <div className="col-sm-9">
          {hasCardOnFile && (
            <div className="form-check">
              <input
                className="form-check-input"
                type="radio"
                id="paymentMethodCreditCardOnFile"
                name="paymentMethod"
                value={SERVICE_PLAN_PAYMENT_METHOD.CREDIT_CARD_ON_FILE}
                checked={paymentMethod === SERVICE_PLAN_PAYMENT_METHOD.CREDIT_CARD_ON_FILE}
                onChange={() => onPaymentMethodChange(
                  SERVICE_PLAN_PAYMENT_METHOD.CREDIT_CARD_ON_FILE,
                )}
              />
              <label className="form-check-label" htmlFor="paymentMethodCreditCardOnFile">
                Credit Card on File &mdash;
                {'  '}
                <FontAwesomeIcon icon={faCreditCard} />
                {'  '}
                {cardOnFileDisplay}
              </label>
            </div>
          )}
          <div className="form-check">
            <input
              className="form-check-input"
              type="radio"
              id="paymentMethodNewCreditCard"
              name="paymentMethod"
              value={SERVICE_PLAN_PAYMENT_METHOD.NEW_CREDIT_CARD}
              checked={paymentMethod === SERVICE_PLAN_PAYMENT_METHOD.NEW_CREDIT_CARD}
              onChange={() => onPaymentMethodChange(SERVICE_PLAN_PAYMENT_METHOD.NEW_CREDIT_CARD)}
            />
            <label className="form-check-label" htmlFor="paymentMethodNewCreditCard">
              New Credit Card &mdash;
              {'  '}
              <FontAwesomeIcon icon={faCreditCard} />
            </label>
          </div>
          {!hideCheckOrBankTransfer && (
            <div className="form-check">
              <input
                className="form-check-input"
                type="radio"
                id="paymentMethodCheckOrBankTransfer"
                name="paymentMethod"
                value={SERVICE_PLAN_PAYMENT_METHOD.CHECK_OR_BANK_TRANSFER}
                checked={paymentMethod === SERVICE_PLAN_PAYMENT_METHOD.CHECK_OR_BANK_TRANSFER}
                onChange={() => onPaymentMethodChange(
                  SERVICE_PLAN_PAYMENT_METHOD.CHECK_OR_BANK_TRANSFER,
                )}
              />
              <label className="form-check-label" htmlFor="paymentMethodCheckOrBankTransfer">
                Check or Bank Transfer &mdash;
                {'  '}
                <FontAwesomeIcon icon={faEnvelope} />
              </label>
            </div>
          )}
        </div>
      </div>

      {showCardFields && (
        <FormErrorHandler errorList={errorList} fieldList={['cardNumber', 'cardCvv']} noDivTags>
          <div className="row mb-3">
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label className="col-form-label col-sm-3 text-sm-right"><b>Card Number</b></label>
            <div className="col-sm-9 col-xl-4 col-lg-6">
              <AltErrorWrapper fieldId="cardNumber">
                <input
                  type="text"
                  className="form-control"
                  value={cardNumber}
                  onChange={(e) => onCardNumberChange(e.target.value)}
                />
              </AltErrorWrapper>
            </div>
          </div>

          <div className="row mb-3">
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label className="col-form-label col-sm-3 text-sm-right"><b>Expiration Date</b></label>
            <div className="col-auto">
              <select
                className="form-control"
                value={expirationMonth}
                onChange={(e) => onExpirationMonthChange(e.target.value)}
              >
                {MONTHS.map((month) => (
                  <option key={month.value} value={month.value}>{month.display}</option>
                ))}
              </select>
            </div>
            <div className="col-auto">
              <select
                className="form-control"
                value={expirationYear}
                onChange={(e) => onExpirationYearChange(e.target.value)}
              >
                {YEARS.map((year) => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="row mb-3">
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label className="col-form-label col-sm-3 text-sm-right"><b>CVV</b></label>
            <div className="col-auto">
              <AltErrorWrapper fieldId="cardCvv">
                <input
                  type="text"
                  className="form-control"
                  style={{ width: '100px' }}
                  value={cvv}
                  onChange={(e) => onCvvChange(e.target.value)}
                />
              </AltErrorWrapper>
            </div>
          </div>
        </FormErrorHandler>
      )}

      {showCheckInfo && (
        <div className="row mb-3">
          <div className="offset-sm-3 col-sm-9">
            <p><b>Checks can be mailed to...</b></p>
            <p>
              Accounts Payable
              <br />
              AVTECH Software, Inc.
              <br />
              16 Cutler Street
              <br />
              Warren, RI 02885
            </p>
            <p><b>Bank transfers can be arranged by contacting our team.</b></p>
            <p>
              <a href="mailto:accountspayable@roomalert.com">accountspayable@roomalert.com</a>
              <br />
              888.220.6700
              <br />
              401.628.1600
            </p>
          </div>
        </div>
      )}

      {showSubmit && (
        <>
          <div className="row mb-3">
            <div className="offset-sm-3 col-sm-9">
              <p>
                By submitting this form, you authorize AVTECH Software, Inc.
                to use this payment method for future renewals. You may
                cancel renewals at any time.
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

PaymentMethodForm.propTypes = {
  paymentMethod: PropTypes.oneOf(Object.values(SERVICE_PLAN_PAYMENT_METHOD)).isRequired,
  onPaymentMethodChange: PropTypes.func.isRequired,
  hasCardOnFile: PropTypes.bool,
  cardOnFileDisplay: PropTypes.string,
  cardNumber: PropTypes.string,
  onCardNumberChange: PropTypes.func,
  expirationMonth: PropTypes.string,
  onExpirationMonthChange: PropTypes.func,
  expirationYear: PropTypes.string,
  onExpirationYearChange: PropTypes.func,
  cvv: PropTypes.string,
  onCvvChange: PropTypes.func,
  errorList: PropTypes.arrayOf(PropTypes.shape({
    field: PropTypes.string,
    message: PropTypes.string,
  })),
  hideCheckOrBankTransfer: PropTypes.bool,
};

PaymentMethodForm.defaultProps = {
  hasCardOnFile: false,
  cardOnFileDisplay: '',
  cardNumber: '',
  onCardNumberChange: () => {},
  expirationMonth: '',
  onExpirationMonthChange: () => {},
  expirationYear: '',
  onExpirationYearChange: () => {},
  cvv: '',
  onCvvChange: () => {},
  errorList: [],
  hideCheckOrBankTransfer: false,
};

export default PaymentMethodForm;
